import {FieldErrors} from 'react-hook-form'

import {FieldNames} from '../../fields/types'
import {normalizeFieldName} from '../../fields/utils/fieldNames.utils'

/**
 * Extracts all validation error messages for a field and its nested subfields.
 * Handles both simple field paths (e.g., 'quantity.volume') and complex field objects.
 *
 * @param errors - React Hook Form errors object
 * @param fieldName - Field name to get errors for (string path or field object)
 * @returns Array of error messages from the field and all its nested subfields
 *
 * @example
 * // Simple field path
 * getFieldErrors(errors, 'quantity.volume')
 * // => ['Quantity is required']
 *
 * @example
 * // Complex field object
 * getFieldErrors(errors, { field: 'dateTime', fieldPart: 'date' })
 * // => ['Date is required', 'Date must be in future']
 *
 * @example
 * // Nested structure errors
 * getFieldErrors(errors, 'mix')
 * // => ['Material is required', 'Contract number invalid']
 */
export function getFieldErrors(errors: FieldErrors, fieldName: FieldNames): string[] {
  const normalizedField = normalizeFieldName(fieldName)
  const path = normalizedField.field
  const segments = path.split('.')

  // 1. Find parent node (one level above the target path)
  let parent: any = errors
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]
    if (!parent || typeof parent !== 'object') {
      parent = undefined
      break
    }
    parent = parent[seg]
  }

  // 2. Find the target node (root of the subtree we want to check)
  let node: any = errors
  for (const seg of segments) {
    if (!node || typeof node !== 'object') {
      node = undefined
      break
    }
    node = node[seg]
  }

  const messages: string[] = []

  const pushMessageIfAny = (n: any) => {
    if (n && typeof n === 'object' && 'message' in n && n.message) {
      messages.push(String(n.message))
    }
  }

  // 3. Check for error at parent level
  pushMessageIfAny(parent)

  // 4. Recursively collect all errors from the target node and its subtree
  const visit = (n: any) => {
    if (!n) return

    if (typeof n === 'object' && 'message' in n && n.message) {
      messages.push(String(n.message))
    }

    if (typeof n !== 'object') return

    Object.values(n).forEach(visit)
  }

  visit(node)

  return messages
}
